/**
 * This component is a resolver. It is meant to be used by page templates using
 * data from a CMS.
 *
 * You can send the data you receive from the CMS to the props of this components
 * by spreading them (like <ListOfEvents {...cmsData} />)
 * and then make these props fit the final module.
 *
 * Note that propTypes are not needed in this component: the GraphQL schema
 * definition is sufficient for that matter.
 */
import React from "react"
import { graphql } from "gatsby"
import { Section } from "@bw/bits"
import { ListFilters } from "@bw/partials"
import { ListOfEvents as ListOfEventsModule } from "@bw/modules"
import useCollectionEvents from "hooks/useCollectionEvents"

const ListOfEvents = ({
  suptitle,
  title,
  subtitle,
  button,
  buttonPosition,
  idAttribute,
  classAttribute,
  category,
  eventType,
  eventsToShow,
  maxItems,
  numberOfColumns,
  groupByDate,
  backgroundVariant,
  isFollowedByModuleWithBackgroundVariant,
  displayFilters,
}) => {
  const events = useCollectionEvents({
    category: category?.slug,
    type: eventType?.slug,
    limit: maxItems,
    eventsToShow: eventsToShow.value,
  })

  const columns = numberOfColumns.value
    ? parseInt(numberOfColumns.value)
    : undefined

  return (
    <Section
      {...{
        suptitle,
        title,
        subtitle,
        button,
        buttonPosition,
        idAttribute,
        classAttribute,
        backgroundVariant,
        isFollowedByModuleWithBackgroundVariant,
      }}
    >
      {displayFilters ? (
        <ListFilters items={events}>
          {({ items }) => (
            <ListOfEventsModule
              events={items}
              groupByDate={groupByDate}
              numberOfColumns={columns}
            />
          )}
        </ListFilters>
      ) : (
        <ListOfEventsModule
          events={events}
          groupByDate={groupByDate}
          numberOfColumns={columns}
        />
      )}
    </Section>
  )
}

/**
 * This generates a GraphQL Fragment that can be used in a page query:
 *
 * Example:
 * ... on ListOfEventsModule {
 *   ...ListOfEventsModuleFragment
 * }
 *
 * This is also what you will get in the props of your ListOfEvents
 * resolver component.
 */
export const query = graphql`
  fragment ListOfEventsModuleFragment on ListOfEventsModule {
    displayFilters
    category {
      slug
    }
    eventType {
      slug
    }
    eventsToShow {
      value
    }
    maxItems
    groupByDate
    numberOfColumns {
      value
    }
  }
`

export { ListOfEvents }
